import { Box, Button, Grid, ListItemText, Typography, useTheme } from "@mui/material"
import { Formik } from "formik"
import { t } from "i18next"
import React, { useContext, useEffect } from "react"
import DefaultButton from "../Button"
import { CircleIcon } from "../icons/CircleIcon"
import CloseSidebarButton from "../navigation/CloseSidebarButton"
import { RouterPrompt } from "../RouterPrompt"
import { SidebarProps2 } from "./Sidebar.interfaces"
import { StyledStickyButtonBox, StyledChildrenWrapper, StyledListItemHeaderGrid, StyledListWrapper, StyledSidebarDrawer } from "./Sidebar.styled"
import { AppContext } from "../AppContextProvider";
import FormComponents from "components/Form/FormComponents/FormComponents"

export const Sidebar = ({
  initialFormikValues,
  setInitialFormikValues,
  formResult,
  formData,
  handleSubmit,
  validationSchema,
  formRef,
  children,
  fields,
  infoText
}: SidebarProps2) => {
  const appContext = useContext(AppContext)
  const isSidebarOpen = appContext.isSidebarOpen === formData.formId || appContext.isSidebarOpen === "Default"
  const theme = useTheme()

  const toggleSidebar = () => {
    if (appContext.isSidebarOpen === "Default") {
      appContext.toggleSideBar("Default")
    } else {
      appContext.toggleSideBar(formData.formId)
    }
  }

  useEffect(() => {
    if (formResult && formResult?.status === 2) {
      setInitialFormikValues(initialFormikValues)
      if (formRef) {
        formRef?.current?.resetForm()
      }
    }
  }, [formResult])

  return (
    <StyledSidebarDrawer variant="persistent" anchor="right" open={isSidebarOpen}>
      <Formik
        initialValues={initialFormikValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          handleSubmit({ ...values })
        }}
        enableReinitialize={true}
        innerRef={formRef}>
        {(formikProps) => (
          <form
            style={{ height: "100%" }}
            id={formData.formId}
            onSubmit={formikProps.handleSubmit}
            noValidate>
            <RouterPrompt />
            <StyledListWrapper>
              <CloseSidebarButton
                toggleMenu={() => {
                  toggleSidebar()
                }}
              />

              <Grid container pb={3} px={{ xs: 3, sm: 4 }} mt={0} pt={12}>
                <StyledListItemHeaderGrid xs={12} md={12} pb={3}>
                  <Box display="flex" alignItems="center">
                    {formData.icon ? <CircleIcon
                      icon={formData.icon}
                      backgroundColor={(theme.palette as any).primary[300]}
                      color={(theme.palette as any).primary[800]}
                      size="lg"
                      sx={{ marginRight: "14px" }}
                    /> : null}
                    <ListItemText
                      primary={
                        <Typography color="textPrimary" variant="h2">
                          {formikProps.values.id < 1 ? formData.titleNew : formData.titleEdit}
                        </Typography>
                      }
                      secondary={
                        <Typography color="textSecondary" variant="bodySmall">
                          {formikProps.values.id < 1 ? formData.subTitleNew : formData.subTitleEdit}
                        </Typography>
                      }
                    />
                  </Box>
                  {infoText && (
                    <Typography mt={3} color="secondary" variant="bodySmall" style={{ color: theme.palette.grey[400] }}>
                      {infoText}
                    </Typography>
                  )}
                </StyledListItemHeaderGrid>
              </Grid>
              <StyledChildrenWrapper px={{ xs: 3, sm: 4 }} pb={8}>
                {children}
                {fields &&
                  <Grid display="flex" direction="column" gap={1} pt={1}>
                    {Object.keys(fields).map((key) => {
                      const field = fields[key]
                      return (
                        <FormComponents
                          field={field}
                          initialValues={initialFormikValues}
                          handleNext={() => { }}
                          formikProps={formikProps}
                        />
                      )
                    })}
                  </Grid>
                }
              </StyledChildrenWrapper>
              <StyledStickyButtonBox p={{ xs: 3, sm: 4 }}>
                <DefaultButton sx={{ width: "100%" }} result={formResult} type="submit">
                  {formikProps.values.id > 0 ? formData.submitEditText : formData.submitText}
                </DefaultButton>
                <Button
                  variant={"outlined"}
                  onClick={() => {
                    toggleSidebar()
                  }}>
                  {t("global.buttons.cancel")}
                </Button>
              </StyledStickyButtonBox>
            </StyledListWrapper>
          </form>
        )}
      </Formik>
    </StyledSidebarDrawer>
  )
}
